import { useMemo, useState } from 'react';

import {
  FolderType,
  moveToFolder,
  selectFinalCut,
  selectProject,
  selectStory,
} from 'components/app/_mediator/app-data.slice';
import { ContextMenu, MenuTrigger, MenuOption } from 'components/UI/context-menu/context-menu';
import { ContextMenuId } from 'constants/context-menu';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

type MenuData = {
  type: FolderType;
};

type Payload = {
  id: StoryId | ProjectId | FinalcutId | FileId;
  folderId: FolderId | null;
};

interface Props extends MenuData, React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
}

export const HeaderActionFolder = ({ disabled, type, children }: Props) => {
  const folders = useAppSelector((state) => state.app__data.folders);
  const files = useAppSelector((state) => state.app__files.files);
  const selectedFile = useAppSelector((state) => state.projects.selectedFile);
  const projectData = useAppSelector(selectProject);
  const storyData = useAppSelector(selectStory);
  const fcData = useAppSelector(selectFinalCut);
  const dispatch = useAppDispatch();

  const [active, setActive] = useState(false);

  const menuId = useMemo(() => {
    return ContextMenuId.folderActions + '__' + type;
  }, [type]);

  const payload: Payload | undefined = useMemo(() => {
    if (!active) return;
    switch (type) {
      case 'project': {
        if (!projectData) return;
        return { id: projectData.projectId, folderId: projectData.folderId };
      }
      case 'file': {
        if (!selectedFile) return;
        const fileData = files[selectedFile];
        if (!fileData) return;
        return { id: fileData.id, folderId: fileData.folderId };
      }
      case 'story': {
        if (!storyData) return;
        return { id: storyData.storyId, folderId: storyData.folderId };
      }
      case 'finalcut': {
        if (!fcData) return;
        return { id: fcData.id, folderId: fcData.folderId };
      }
    }
  }, [active]);

  const options = useMemo(() => {
    if (!payload || !folders) return [];
    const options = [
      { value: -1, label: 'Restore' },
      ...folders.map((el) => ({ value: el.id, label: `Move to ${el.name}` })),
    ];
    return options.filter((el) => el.value !== (payload.folderId ?? -1));
  }, [payload, folders]);

  const handleMove = (folderId: FolderId | -1) => {
    if (!payload) return;
    dispatch(moveToFolder({ entity: type, entityId: payload.id, folderId }));
  };

  return (
    <>
      <MenuTrigger
        id={menuId}
        collect={() => ({ type }) as MenuData}
        holdToDisplay={-1}
        mouseButton={0}
        disable={disabled}
      >
        {children}
      </MenuTrigger>
      <ContextMenu id={menuId} onShow={() => setActive(true)} onHide={() => setActive(false)}>
        {options.map((el) => (
          <MenuOption key={el.value} onClick={() => handleMove(el.value)}>
            {el.label}
          </MenuOption>
        ))}
      </ContextMenu>
    </>
  );
};
